import { useState } from "react";
import { cn } from "@strivelabs/ui-core";
import { GlobeIcon } from "lucide-react";

/** Hostname without the leading `www.`, falling back to the raw string. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Google's favicon service — hostname-scoped, cheap, browser-cached. */
export function faviconOf(url: string): string {
  // `hostOf` can fall back to the raw string on parse failure; encode it so
  // spaces / `&` / `#` can't corrupt the query params.
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostOf(url))}&sz=64`;
}

/** Bare-domain matcher (e.g. `justdial.com`, `sub.example.co.uk`) — no spaces, no path. */
const DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/i;

/** True when `text` reads as a bare domain — the shape model-emitted citations take. */
export function looksLikeDomain(text: string): boolean {
  return DOMAIN_RE.test(text.trim());
}

/**
 * Site favicon with a graceful fallback to a neutral globe glyph when the
 * favicon can't be fetched (blocked, 404, offline).
 */
export function Favicon({ url, className }: { url: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const [prevUrl, setPrevUrl] = useState(url);

  // Reset the fallback when this instance is reused for a different url (e.g.
  // React reusing the node at the same tree position while streaming), so a
  // prior load failure doesn't leave a valid new favicon stuck on the globe.
  if (url !== prevUrl) {
    setPrevUrl(url);
    setFailed(false);
  }

  if (failed) {
    return <GlobeIcon className={cn("text-custom-text-secondary", className)} aria-hidden />;
  }

  return (
    <img
      src={faviconOf(url)}
      alt=""
      className={cn("object-contain", className)}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
